import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";
import AcceptInvitation from "./pages/AcceptInvitation";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LoginHelp from "./pages/LoginHelp";
import Dashboard from "./pages/Dashboard";
import Inventory from "./pages/Inventory";
import Items from "./pages/Items";
import Restock from "./pages/Restock";
import Usage from "./pages/Usage";
import Transfer from "./pages/Transfer";
import Reports from "./pages/Reports";
import Users from "./pages/admin/Users";
import Categories from "./pages/admin/Categories";
import Brands from "./pages/admin/Brands";
import Inventories from "./pages/admin/Inventories";
import Vendors from "./pages/admin/Vendors";
import DataManagement from "./pages/admin/DataManagement";
import Settings from "./pages/admin/Settings";
import JobSummary from "./pages/JobSummary";
import VendorAnalytics from "./pages/VendorAnalytics";
import ItemAnalytics from "./pages/ItemAnalytics";
import PurchaseHistory from "./pages/PurchaseHistory";

// Loading spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
  </div>
);

// Stable role arrays (prevents new references on each render)
const ADMIN_ONLY = ['admin'];
const ADMIN_MANAGER = ['admin', 'manager'];
const ALL_ROLES = ['admin', 'manager', 'technician'];

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, needsPasswordSetup } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to set-password if user hasn't initialized their password
  if (needsPasswordSetup()) {
    return <Navigate to="/set-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Set Password route - requires auth but NOT password_initialized
const SetPasswordRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If password is already initialized, go to dashboard
  if (user?.password_initialized) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SetPassword />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />

      {/* Accept Invitation - Public, no auth needed */}
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login-help" element={<LoginHelp />} />

      {/* Set Password - Auth required but no password_initialized */}
      <Route path="/set-password" element={<SetPasswordRoute />} />

      {/* Protected Routes - All Roles */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/inventory" element={
        <ProtectedRoute>
          <Inventory />
        </ProtectedRoute>
      } />
      <Route path="/usage" element={
        <ProtectedRoute>
          <Usage />
        </ProtectedRoute>
      } />

      {/* Protected Routes - Manager & Admin */}
      <Route path="/items" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Items />
        </ProtectedRoute>
      } />
      <Route path="/restock" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Restock />
        </ProtectedRoute>
      } />
      <Route path="/transfer" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Transfer />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Reports />
        </ProtectedRoute>
      } />
      <Route path="/job-summary" element={
        <ProtectedRoute allowedRoles={ALL_ROLES}>
          <JobSummary />
        </ProtectedRoute>
      } />
      <Route path="/vendor-analytics" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <VendorAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/item-analytics" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <ItemAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/purchases" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <PurchaseHistory />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={ADMIN_ONLY}>
          <Users />
        </ProtectedRoute>
      } />
      <Route path="/admin/categories" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Categories />
        </ProtectedRoute>
      } />
      <Route path="/admin/brands" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Brands />
        </ProtectedRoute>
      } />
      <Route path="/admin/inventories" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Inventories />
        </ProtectedRoute>
      } />
      <Route path="/admin/vendors" element={
        <ProtectedRoute allowedRoles={ADMIN_MANAGER}>
          <Vendors />
        </ProtectedRoute>
      } />
      <Route path="/admin/data" element={
        <ProtectedRoute allowedRoles={ADMIN_ONLY}>
          <DataManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={ADMIN_ONLY}>
          <Settings />
        </ProtectedRoute>
      } />

      {/* Redirect /register to login */}
      <Route path="/register" element={<Navigate to="/login" replace />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
